import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Card } from './ui/Card';
interface Update {
  title: string;
  date?: string;
  summary: string;
  tag: string;
  link?: string;
  color: string;
}
interface EditorialLayoutProps {
  updates: Update[];
}
export function EditorialLayout({
  updates
}: EditorialLayoutProps) {
  // First item is featured
  const featuredUpdate = updates[0];
  const regularUpdates = updates.slice(1);
  return <div className="space-y-8" data-id="element-90">
      {/* Featured Hero Card */}
      {featuredUpdate && <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} data-id="element-91">
          {featuredUpdate.link ? <a href={featuredUpdate.link} target="_blank" rel="noopener noreferrer" className="block group" data-id="element-92">
              <Card className="p-10 md:p-14 bg-[#1c3f4d]/40 backdrop-blur-sm border-white/10 transition-all duration-500 group-hover:border-vitalityTeal/50 group-hover:shadow-2xl group-hover:shadow-vitalityTeal/10" style={{
          borderColor: `${featuredUpdate.color}40`
        }} data-id="element-93">
                <div className="flex flex-col md:flex-row md:items-start gap-8" data-id="element-94">
                  <div className="flex-1" data-id="element-95">
                    <div className="flex items-center gap-4 mb-6" data-id="element-96">
                      <span className="px-4 py-1.5 rounded-full text-sm font-bold border uppercase tracking-wider" style={{
                  color: featuredUpdate.color,
                  borderColor: `${featuredUpdate.color}50`,
                  backgroundColor: `${featuredUpdate.color}10`
                }} data-id="element-97">
                        Featured • {featuredUpdate.tag}
                      </span>
                    </div>

                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight group-hover:text-white transition-colors" data-id="element-98">
                      {featuredUpdate.title}
                    </h2>

                    <p className="text-xl text-gray-300 leading-relaxed max-w-3xl" data-id="element-99">
                      {featuredUpdate.summary}
                    </p>
                  </div>

                  <div className="md:pt-2" data-id="element-100">
                    <div className="w-16 h-16 rounded-full border flex items-center justify-center transition-all duration-300 group-hover:bg-vitalityTeal group-hover:border-vitalityTeal group-hover:scale-110" style={{
                borderColor: `${featuredUpdate.color}50`
              }} data-id="element-101">
                      <ArrowUpRight className="w-8 h-8 transition-colors duration-300 group-hover:text-[#0e2533]" style={{
                  color: featuredUpdate.color
                }} data-id="element-102" />
                    </div>
                  </div>
                </div>
              </Card>
            </a> : <Card className="p-10 md:p-14 bg-[#1c3f4d]/40 backdrop-blur-sm border-white/10" style={{
        borderColor: `${featuredUpdate.color}40`
      }} data-id="element-103">
              <div className="flex flex-col md:flex-row md:items-start gap-8" data-id="element-104">
                <div className="flex-1" data-id="element-105">
                  <div className="flex items-center gap-4 mb-6" data-id="element-106">
                    <span className="px-4 py-1.5 rounded-full text-sm font-bold border uppercase tracking-wider" style={{
                color: featuredUpdate.color,
                borderColor: `${featuredUpdate.color}50`,
                backgroundColor: `${featuredUpdate.color}10`
              }} data-id="element-107">
                      Featured • {featuredUpdate.tag}
                    </span>
                  </div>

                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-108">
                    {featuredUpdate.title}
                  </h2>

                  <p className="text-xl text-gray-300 leading-relaxed max-w-3xl" data-id="element-109">
                    {featuredUpdate.summary}
                  </p>
                </div>
              </div>
            </Card>}
        </motion.div>}

      {/* Grid for remaining updates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-110">
        {regularUpdates.map((update, index) => {
        // Make every 3rd item span full width for visual variety
        const isWide = (index + 1) % 3 === 0;
        const colSpan = isWide ? 'md:col-span-2' : '';
        return <motion.div key={update.title} className={colSpan} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} data-id="element-111">
              {update.link ? <a href={update.link} target="_blank" rel="noopener noreferrer" className="block group h-full" data-id="element-112">
                  <Card className={`h-full ${isWide ? 'p-10' : 'p-8'} flex flex-col bg-[#1c3f4d]/40 backdrop-blur-sm border-white/10 transition-all duration-300 group-hover:border-vitalityTeal/50 group-hover:-translate-y-1 group-hover:shadow-lg group-hover:shadow-vitalityTeal/5`} style={{
              borderColor: `${update.color}30`
            }} data-id="element-113">
                    <div className="flex justify-between items-start mb-6" data-id="element-114">
                      <span className="px-3 py-1 rounded-full text-xs font-medium border uppercase tracking-wider" style={{
                  color: update.color,
                  borderColor: `${update.color}50`,
                  backgroundColor: `${update.color}10`
                }} data-id="element-115">
                        {update.tag}
                      </span>
                      <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" style={{
                  color: update.color
                }} data-id="element-116" />
                    </div>

                    <h3 className={`${isWide ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-4 group-hover:text-white transition-colors leading-tight`} data-id="element-117">
                      {update.title}
                    </h3>

                    <p className={`text-gray-300 leading-relaxed ${isWide ? 'text-lg' : 'text-base'}`} data-id="element-118">
                      {update.summary}
                    </p>
                  </Card>
                </a> : <Card className={`h-full ${isWide ? 'p-10' : 'p-8'} flex flex-col bg-[#1c3f4d]/40 backdrop-blur-sm border-white/10`} style={{
            borderColor: `${update.color}30`
          }} data-id="element-119">
                  <div className="flex justify-between items-start mb-6" data-id="element-120">
                    <span className="px-3 py-1 rounded-full text-xs font-medium border uppercase tracking-wider" style={{
                color: update.color,
                borderColor: `${update.color}50`,
                backgroundColor: `${update.color}10`
              }} data-id="element-121">
                      {update.tag}
                    </span>
                  </div>

                  <h3 className={`${isWide ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-4 leading-tight`} data-id="element-122">
                    {update.title}
                  </h3>

                  <p className={`text-gray-300 leading-relaxed ${isWide ? 'text-lg' : 'text-base'}`} data-id="element-123">
                    {update.summary}
                  </p>
                </Card>}
            </motion.div>;
      })}
      </div>
    </div>;
}